/**
 * Permission hooks — Phase 5.
 *
 * Idiomatic React layer over the existing `useAuthStore` permission
 * state. Components should prefer these hooks to reading the store
 * directly: they're typed against the canonical `Permission` union
 * from `@invento/shared`, so callers cannot typo a code, and the
 * Zustand selector form keeps re-renders limited to permission
 * changes.
 *
 * SECURITY BOUNDARY (per FRONTEND_AUTH_PERMISSIONS.md):
 *   These hooks are UX hints ONLY. Backend authorization is the
 *   real boundary. A determined user can edit local state; that's
 *   fine — every API call is permission-checked server-side, and
 *   the dashboard's 403 path renders a denied state without
 *   logging the user out.
 *
 * Co-located with `has-business-context.ts` (the precedent set by
 * 4.4c-3) so the four canonical session predicates live together.
 */

import { useAuthStore } from '@/state/stores/auth.store';
import type { Permission } from '@invento/shared';

/**
 * Returns true iff the current user has the given permission.
 * Returns false when the user is null (logged out) — defensive
 * default, never blocks rendering with an undefined.
 */
export function useHasPermission(permission: Permission): boolean {
  return useAuthStore((state) =>
    state.user ? state.user.permissions.includes(permission) : false,
  );
}

/**
 * Returns true iff the current user has AT LEAST ONE of the given
 * permissions. Empty array → false (caller asked "has any of [nothing]"
 * which is trivially false).
 */
export function useHasAnyPermission(permissions: readonly Permission[]): boolean {
  return useAuthStore((state) => {
    if (!state.user) return false;
    if (permissions.length === 0) return false;
    return permissions.some((p) => state.user!.permissions.includes(p));
  });
}

/**
 * Returns true iff the current user has ALL of the given permissions.
 * Empty array → true (vacuous truth: "has all of [nothing]" is true).
 * That matches `Array.prototype.every`'s behavior on an empty array
 * and means a guard with no requirements never blocks.
 */
export function useHasAllPermissions(permissions: readonly Permission[]): boolean {
  return useAuthStore((state) => {
    if (!state.user) return false;
    if (permissions.length === 0) return true;
    return permissions.every((p) => state.user!.permissions.includes(p));
  });
}
